import { useState } from 'react';
import {
  Box, Button, TextField, Snackbar, IconButton, InputAdornment, Stack,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../common/components/LocalizationProvider';
import LoginLayout from './LoginLayout';
import LogoImage from '../common/auth/LogoImage';
import AuthCard from '../common/auth/AuthCard';

const useStyles = makeStyles()((theme) => ({
  input: {
    width: "100%",
    padding: "14px 16px",
    borderRadius: "8px",
    background: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(0, 0, 0, 0.05)",
    "&:focus-within": {
      background: theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.15)"
      : "rgba(0, 0, 0, 0.08)",
    },
  },
  primaryButton: {
    background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: "white",
    fontWeight: "bold",
    padding: "14px 0",
    borderRadius: "8px",
    fontSize: "1rem",
    minHeight: "44px",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 6px 8px rgba(0, 0, 0, 0.15)",
    },
    transition: "all 0.2s ease",
  },
  secondaryButton: {
    padding: "10px 0",
    fontWeight: 600,
    minHeight: "40px",
  },
}));

const ResetPasswordPage = () => {
  const { classes } = useStyles();
  const t = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [snackbar, setSnackbar] = useState('');

  const handleReset = async (event) => {
    event.preventDefault();
    try {
      const response = await fetch('/api/users/send-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (response.ok) {
        setSnackbar(t('loginResetEmailSent'));
        setEmail('');
      } else {
        setSnackbar(await response.text());
      }
    } catch (error) {
      setSnackbar(error.message);
    }
  };

  return (
    <LoginLayout>
      <Box
        component="form"
        onSubmit={handleReset}
        sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}
      >
        <AuthCard>
          <LogoImage />

          <TextField
            required
            fullWidth
            placeholder={t("userEmail")}
            name="email"
            value={email}
            autoComplete="email"
            autoFocus
            variant="standard"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon />
                </InputAdornment>
              ),
              className: classes.input,
              disableUnderline: true,
            }}
            onChange={(e) => setEmail(e.target.value)}
          />

          <Stack spacing={1.2}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!email}
              className={classes.primaryButton}
              fullWidth
            >
              {t('loginReset')}
            </Button>
            <Button
              onClick={() => navigate('/login')}
              variant="outlined"
              color="primary"
              className={classes.secondaryButton}
              fullWidth
            >
              {t('loginLogin')}
            </Button>
          </Stack>
        </AuthCard>
      </Box>

      {/* Snackbar mensagens */}
      <Snackbar
        open={!!snackbar}
        message={snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar('')}
        action={(
          <IconButton size="small" color="inherit" onClick={() => setSnackbar('')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      />
    </LoginLayout>
  );
};

export default ResetPasswordPage;
